import React, { useEffect, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { Box, Button, Link } from '@mui/material'
import { DataTable } from '../'
import { Loader } from '../../loader'
import { useAcademicApi, useAcademicDataDigest } from '../../../local'
import { SelectOptionControl } from '../../forms'
import { useSelector } from '@ggs/store'
import { size, sortBy, groupBy, keyBy, keys, isArray, isObject } from 'lodash'
import './SubjectsTable.scss'

/**
 * @typedef {import('mui-datatables').MUIDataTableOptions} MUIDataTableOptions
 * @typedef {import('../../../definitions').SubjectItem} SubjectItem
 * @typedef {import('../../../definitions').SubjectList} SubjectList
 * @typedef {import('../../../definitions').AcademicUnitRecord} AcademicUnitRecord
 * @typedef {import('lodash').Dictionary<SubjectItem[]>} SubjectResultsIndex
 * @typedef {import('lodash').Dictionary<AcademicUnitRecord>} AcademicUnitsIndex
 */

/**
 * Course subjects table.
 * @return {JSX.Element}
 */
const SubjectsTable = () => {
  const { t } = useI18n()
  const { getCourseSubjects, getAcademicUnitHierarchies } = useAcademicApi()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const [dataTable, setDataTable] = useState([])

  /**
   * @type {string[]}
   */
  const defaultAlphaFilterOptions = null
  const [alphaFilterOptions, setAlphaFilterOptions] = useState(defaultAlphaFilterOptions)

  /**
   * @type {SubjectResultsIndex}
   */
  const defaultDataTableIndexed = null
  const [dataTableIndexed, setDataTableIndexed] = useState(defaultDataTableIndexed)
  const [activeAlphaFilter, setActiveAlphaFilter] = useState('All')
  const [loading, setLoading] = useState(true)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const campus = useSelector((state) => state.settings.campus || 'UBCV')
  const { digestSubjectsList, composeItemsList } = useAcademicDataDigest()

  /**
   * @type {AcademicUnitsIndex}
   */
  const defaultAcademicUnitsIndex = null
  const [academicUnitsIndex, setAcademicUnitsIndex] = useState(defaultAcademicUnitsIndex)

  // Page size for subjects retrieved with API request.
  const pageSize = 500
  // Rows presented for each table page.
  const rowsPerPage = 20

  /**
   * Resolve academic units hierarchy data and produce index by referenceID.
   */
  const resolveAcademicUnitNames = async () => {
    const academicUnitHierarchiesResult = await getAcademicUnitHierarchies({
      pageSize: 500,
    })

    if (isArray(academicUnitHierarchiesResult.pageItems)) {
      const academicUnitRecords = academicUnitHierarchiesResult.pageItems
      const academicUnitIndex = keyBy(academicUnitRecords, 'referenceId')
      setAcademicUnitsIndex(academicUnitIndex)
    }
  }

  const enableLoading = () => {
    setLoading(true)
  }

  const disableLoading = () => {
    setLoading(false)
  }

  /**
   * Receive current page index.
   * @param {number} currentPage
   */
  const onPageChange = (currentPage) => {
    setCurrentPageIndex(currentPage)
  }

  /**
   * Handle click on subject title.
   * @param {React.MouseEvent<HTMLAnchorElement>} e
   */
  const handleLinkClick = (e) => {
    // Prevent opening target link when running in SB.
    if (isStorybook) {
      e.preventDefault()
      alert('Course subjects page route will open when running in Drupal.')
    }
  }

  /**
   * Compose alphabetical index.
   * @param {SubjectList} dataTable
   * @return {string[]}
   */
  const composeAlphabeticalFilter = (dataTable) => {
    const options = [t('All')]
    const dataTableIndex = groupBy(dataTable, 'alphaIndex')
    setDataTableIndexed(dataTableIndex)

    const alphaOptions = keys(dataTableIndex)
    const uniqResultIndex = sortBy(alphaOptions) || []

    return options.concat(uniqResultIndex)
  }

  /**
   * Augment data table with links to browse course subject page.
   * @param {SubjectList} dataTable
   * @return {SubjectList}
   */
  const augmentWithDerivativeFields = (dataTable) => {
    return dataTable.map((item) => {
      const subjectCode = item.subjectCode || ''
      const academicUnitNames =
        isArray(item.academicUnitId) && isObject(academicUnitsIndex)
          ? item.academicUnitId.map((unitId) => academicUnitsIndex[unitId]?.name || 'Unknown')
          : null
      return {
        ...item,
        alphaIndex: subjectCode.charAt(0),
        academicUnitName: composeItemsList(academicUnitNames || []),
      }
    })
  }

  /**
   * Compose current selected campus academic unit ID.
   * @return {string[]}
   */
  const composeCurrentCampusAcademicUnit = () => {
    const campusKey = campus.slice(-1)
    return [`ACADEMIC_UNIT-UBC-${campusKey}`]
  }

  /**
   * Resolve subject courses data to present in the table.
   */
  const resolveSubjectsData = async () => {
    const response = await getCourseSubjects({
      pageSize,
      academicUnitId: composeCurrentCampusAcademicUnit(),
      isActive: true,
      hasDisplayOnPublicWebsiteTag: true,
    })

    if (!response) {
      setDataTable([])
      return
    }

    const pageDataTable = digestSubjectsList(response.pageItems)
    if (pageDataTable) {
      const augmentedPageDataTable = augmentWithDerivativeFields(pageDataTable)
      setDataTable(augmentedPageDataTable)
      setAlphaFilterOptions(composeAlphabeticalFilter(augmentedPageDataTable))
    }
  }

  /**
   * @type {MUIDataTableOptions}
   */
  const options = {
    page: currentPageIndex,
    onChangePage: onPageChange,
    jumpToPage: false,
    pagination: true,
    sortOrder: {
      name: 'subjectCode',
      direction: 'asc',
    },
    rowsPerPage,
    textLabels: {
      body: {
        noMatch: loading ? t('Please wait...') : t('No results found'),
      },
    },
  }

  /**
   * Filter subjects by alpha index.
   * @param {React.MouseEvent<HTMLButtonElement>} event
   */
  const filterByIndex = (event) => {
    const targetElement = event.currentTarget
    const alphaIndex = targetElement.getAttribute('data-index')
    setActiveAlphaFilter(alphaIndex)
    // Reset page to display the first result of the selected alpha index.
    setCurrentPageIndex(0)
  }

  useEffect(() => {
    enableLoading()
    resolveAcademicUnitNames().then(() => {})
  }, [])

  useEffect(() => {
    if (academicUnitsIndex) {
      enableLoading()
      resolveSubjectsData().then(() => {
        console.log('Resolving subjects data')
        disableLoading()
      })
    }
  }, [campus, academicUnitsIndex])

  if (loading || !dataTableIndexed) {
    return <Loader />
  }

  const finalDataTable = (dataTableIndexed[activeAlphaFilter] || dataTable).filter((entry) => {
    const filterRegex = /\b(Shell|Supplemental|Transition|Tuition)\b/i // Regular expression to match 'Shell', 'Supplemental', 'Transition' or 'Tuition' as whole words, case-insensitively
    return !filterRegex.test(entry.subjectTitle) // Only include entries that do not match the regex
  })

  /**
   * Custom render function for subject code column
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderCodeLink = (dataIndex) => {
    const subjectCode = finalDataTable[dataIndex].subjectCode
    return (
      <Link onClick={handleLinkClick} href={`/browse-courses/subject/${subjectCode}`}>
        {subjectCode}
      </Link>
    )
  }

  /**
   * Custom render function for subject title column
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderTitleLink = (dataIndex) => {
    const subjectCode = finalDataTable[dataIndex].subjectCode
    const value = finalDataTable[dataIndex].subjectTitle
    return (
      <Link onClick={handleLinkClick} href={`/browse-courses/subject/${subjectCode}`}>
        {value}
      </Link>
    )
  }

  const tableColumns = [
    {
      name: 'subjectCode',
      label: 'Subject Code',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderCodeLink,
      },
    },
    {
      name: 'subjectTitle',
      label: 'Subject Title',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderTitleLink,
      },
    },
  ]

  const rowsCount = size(finalDataTable)

  return (
    <Box>
      {alphaFilterOptions && (
        <div className="subjects-table-filters">
          <div className="subjects-table-filters__filter-buttons">
            {alphaFilterOptions.map((option) => (
              <Button
                className="subjects-table-filters__btn"
                onClick={filterByIndex}
                key={option}
                data-index={option}
                data-active={option === activeAlphaFilter}
              >
                {option}
              </Button>
            ))}
          </div>
          <div className="subjects-table-filters__filter-dropdown">
            <SelectOptionControl
              id="page-index"
              options={alphaFilterOptions.map((option) => ({ label: option, value: option }))}
              defaultValue={activeAlphaFilter}
              selectLabel={t('Filter Course Subjects')}
              onSelectOption={setActiveAlphaFilter}
            />
          </div>
        </div>
      )}
      <DataTable
        className="data-table"
        title={''}
        data={finalDataTable}
        columns={tableColumns}
        options={{
          ...options,
          count: rowsCount,
        }}
      />
    </Box>
  )
}

export default SubjectsTable